import React, { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styles from './css/index.module.css';

import { Dismiss16Regular } from '@fluentui/react-icons';
import { PostIRLDocuments } from '../../redux/documentsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { createDocxBlobFromHtml } from '../Journal/exportNote';
import { sanitizeRichTextForFileExport } from '../../utils/sanitizeRichTextForFileExport';

import { toast } from 'react-toastify';

const EMPTY_QUILL_CONTENT = '<p><br></p>';

const FILE_FORMATS = [
    { value: 'txt', label: 'Text (.txt)', extension: 'txt', mimeType: 'text/plain' },
    { value: 'docx', label: 'Word (.docx)', extension: 'docx', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
];

const isEmptyContent = html => !html || html === EMPTY_QUILL_CONTENT || html.replace(/<[^>]*>/g, '').trim() === '';

const sanitizeFileName = name =>
    name
        .trim()
        .replace(/[/\\?%*:|"<>]/g, '')
        .replace(/\.(txt|docx)$/i, '');

const htmlToPlainText = html => {
    if (typeof document === 'undefined') {
        return html.replace(/<[^>]*>/g, '');
    }
    const el = document.createElement('div');
    el.innerHTML = html;
    return el.innerText || el.textContent || '';
};

const buildFileFromContent = async (format, fileName, content) => {
    const formatConfig = FILE_FORMATS.find(item => item.value === format) || FILE_FORMATS[0];

    if (format === 'docx') {
        const blob = await createDocxBlobFromHtml({ bodyHtml: content, includeLogo: false, includeTitle: false });
        return { blob, extension: formatConfig.extension, mimeType: formatConfig.mimeType };
    }

    if (format === 'txt') {
        const blob = new Blob([htmlToPlainText(content)], { type: formatConfig.mimeType });
        return { blob, extension: formatConfig.extension, mimeType: formatConfig.mimeType };
    }

    throw new Error(`Unsupported file format: ${format}`);
};

const AddTextDocSidebar = ({ setAddTextDocSidebar, setShowSidebar, selectedFolders, workstream, NavigationMenuFolders }) => {
    const selectedFolder = useMemo(() => {
        return selectedFolders?.length > 0 ? selectedFolders[selectedFolders.length - 1] : {};
    }, [selectedFolders]);
    const { selectedProject } = useSelector(state => state?.projectData);
    const { projectUsersAll } = useSelector(state => state?.team);
    const { user } = useSelector(state => state?.user);
    const [buttonLoading, setButtonLoading] = React.useState(false);
    const dispatch = useDispatch();

    const [fileName, setFileName] = React.useState('');
    const [fileFormat, setFileFormat] = React.useState('txt');
    const [content, setContent] = React.useState('');

    const handleClose = () => {
        setShowSidebar(false);
        setAddTextDocSidebar(false);
        setFileName('');
        setFileFormat('txt');
        setContent('');
    };

    const handleSubmit = async () => {
        const trimmedName = sanitizeFileName(fileName);
        if (!trimmedName) {
            toast.error('Please enter a file name.');
            return;
        }
        if (isEmptyContent(content)) {
            toast.error('Please enter some content.');
            return;
        }

        const sanitizedContent = sanitizeRichTextForFileExport(content);
        if (isEmptyContent(sanitizedContent)) {
            toast.error('Content contains unsupported or unsafe markup and cannot be saved.');
            return;
        }

        setButtonLoading(true);

        try {
            const { blob, extension, mimeType } = await buildFileFromContent(fileFormat, trimmedName, sanitizedContent);
            const fullFileName = `${trimmedName}.${extension}`;
            const file = new File([blob], fullFileName, { type: mimeType });

            const body = new FormData();
            body.append('document', file);
            body.append('document_name', fullFileName);
            body.append('ws_folder', selectedFolder?.id);
            body.append('project', selectedProject?.id);
            body.append('vdr', selectedProject?.vdr?.id);
            if (selectedProject?.vdr?.vdr_admin !== user?.id) {
                body.append('upload_by', projectUsersAll?.find(item => item?.value?.user_id === user?.id)?.value?.group_member_id);
            }

            const res = await dispatch(PostIRLDocuments({ body, ws_id: workstream?.id }));
            setButtonLoading(false);
            if (res?.type === 'POST_IRL_DOC/rejected') {
                toast.error(res?.payload?.detail || 'Failed to save file.');
                return;
            }
            toast.success('File saved successfully.');
            handleClose();
        } catch (error) {
            setButtonLoading(false);
            console.error('Error saving text/doc file:', error);
            toast.error('Failed to save file.');
        }
    };

    return (
        <div className={styles.sidebarWhite}>
            <div className="d-flex align-items-center justify-content-between mb-5">
                <h5 className={styles.sidebarTitle}>Create a File</h5>
                <Dismiss16Regular onClick={handleClose} role="button" color="var(--Neutral-Gray-6)" />
            </div>
            <div className="mb-4">{NavigationMenuFolders}</div>
            <div className="mt-3">
                <label className={styles.fieldLabel}>File Name</label>
                <input
                    value={fileName}
                    onChange={e => setFileName(e?.target?.value)}
                    placeholder="Enter file name"
                    className={styles.inputField + ' form-control mb-3'}
                />
            </div>
            <div className="mb-4">
                <label className={styles.fieldLabel}>Save as</label>
                <select value={fileFormat} onChange={e => setFileFormat(e.target.value)} className={'form-select ' + styles.inputField}>
                    {FILE_FORMATS.map(format => (
                        <option key={format.value} value={format.value}>
                            {format.label}
                        </option>
                    ))}
                </select>
            </div>
            <div className="mb-4">
                <label className={styles.fieldLabel}>Content</label>
                <ReactQuill
                    placeholder="Enter content..."
                    modules={{
                        toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
                    }}
                    theme="snow"
                    className={styles.textDocEditor}
                    value={content}
                    onChange={setContent}
                />
            </div>
            <button onClick={handleSubmit} disabled={buttonLoading} className={styles.saveButton}>
                Save
            </button>
        </div>
    );
};

export default AddTextDocSidebar;
